// @greenlight-spec: 19-design-system.md

import * as React from 'react';

/**
 * Returns a coarse human-readable elapsed-time string for an ISO timestamp,
 * e.g. "2 min ago", "3 h ago", "1 d ago". Exact times belong in tooltips or
 * detail views (post-MVP).
 */
export function relativeTime(iso: string, now: number = Date.now()): string {
  const diffMs = now - new Date(iso).getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH} h ago`;
  const diffD = Math.floor(diffH / 24);
  return `${diffD} d ago`;
}

/**
 * The forward-looking counterpart: "in 4 min", "in 15 h", "in 2 d". A moment
 * already past reads as "due now" rather than as a negative interval — a
 * schedule whose firing is overdue is exactly when a wrong-signed answer is
 * least useful.
 */
export function timeUntil(iso: string, now: number = Date.now()): string {
  const diffMin = Math.floor((new Date(iso).getTime() - now) / 60_000);
  if (diffMin < 1) return 'due now';
  if (diffMin < 60) return `in ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `in ${diffH} h`;
  return `in ${Math.floor(diffH / 24)} d`;
}

/** Sub-minute-aware elapsed string for the audit hero: "just now", "12s ago", "3 min ago". */
export function secondsAgo(iso: string): string {
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 1) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  return `${Math.floor(m / 60)} h ago`;
}

/** Forces a re-render every `ms` so relative-time labels stay current between data refetches. */
export function useTick(ms = 1000): void {
  const [, setN] = React.useState(0);
  React.useEffect(() => {
    const id = window.setInterval(() => setN((n) => n + 1), ms);
    return () => window.clearInterval(id);
  }, [ms]);
}
